/*
 * Artink "Top selling products" carousel. The track is a native scroll-snap row (swipe and
 * keyboard scrolling work without JS); this adds prev/next arrows, a progress line, and
 * mouse drag on desktop. Card reveal and add to cart come from artink-featured-products.js.
 */

function bootBest(root) {
  if (!root || root.dataset.bestBooted === '1') return;
  const track = root.querySelector('[data-best-track]');
  if (!track) return;
  root.dataset.bestBooted = '1';

  const prev = root.querySelector('[data-best-prev]');
  const next = root.querySelector('[data-best-next]');
  const bar = root.querySelector('[data-best-bar]');
  const progress = bar?.parentElement;
  const controller = new AbortController();
  const { signal } = controller;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let frame = 0;

  const isRtl = () => getComputedStyle(track).direction === 'rtl';
  // scrollLeft is negative in RTL in modern browsers; work with the distance from the start.
  const scrolled = () => Math.abs(track.scrollLeft);
  const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

  const update = () => {
    frame = 0;
    const max = maxScroll();
    const pos = scrolled();
    if (prev) prev.disabled = pos <= 2;
    if (next) next.disabled = pos >= max - 2;
    if (bar && progress) {
      const width = progress.clientWidth;
      const ratio = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
      const thumb = Math.max(width * Math.min(1, ratio), 32);
      const x = max ? (pos / max) * (width - thumb) : 0;
      bar.style.setProperty('--best-thumb', `${thumb}px`);
      bar.style.setProperty('--best-x', `${isRtl() ? -x : x}px`);
    }
    root.classList.toggle('is-static', max <= 2);
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(update);
  };

  // Move by the cards that fit in view, so each click shows a fresh set.
  const step = (dir) => {
    const slide = track.querySelector('.artink-best__slide');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const slideW = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth;
    const perView = Math.max(1, Math.floor((track.clientWidth + gap) / slideW));
    const delta = slideW * perView * dir * (isRtl() ? -1 : 1);
    track.scrollBy({ left: delta, behavior: reduce ? 'auto' : 'smooth' });
  };

  prev?.addEventListener('click', () => step(-1), { signal });
  next?.addEventListener('click', () => step(1), { signal });
  track.addEventListener('scroll', schedule, { passive: true, signal });

  // Desktop mouse drag; touch keeps native swipe.
  let dragging = false;
  let moved = false;
  let startX = 0;
  let startLeft = 0;

  track.addEventListener(
    'pointerdown',
    (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target instanceof Element && event.target.closest('button, input, select, textarea')) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startLeft = track.scrollLeft;
    },
    { signal }
  );

  window.addEventListener(
    'pointermove',
    (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) < 6) return;
      if (!moved) {
        moved = true;
        track.classList.add('is-dragging');
      }
      track.scrollLeft = startLeft - dx;
    },
    { signal }
  );

  const endDrag = () => {
    if (!dragging) return;
    dragging = false;
    if (!moved) return;
    track.classList.remove('is-dragging');
    // Let snapping settle on the nearest card after the drag.
    const slide = track.querySelector('.artink-best__slide');
    if (slide) {
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const w = slide.getBoundingClientRect().width + gap;
      const target = Math.round(track.scrollLeft / w) * w;
      track.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
    }
  };
  window.addEventListener('pointerup', endDrag, { signal });
  window.addEventListener('pointercancel', endDrag, { signal });

  // A drag must not also open the product it started on.
  track.addEventListener(
    'click',
    (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    },
    { capture: true, signal }
  );

  let ro;
  if (window.ResizeObserver) {
    ro = new ResizeObserver(schedule);
    ro.observe(track);
  } else {
    window.addEventListener('resize', schedule, { signal });
  }

  update();

  root._artinkBestCleanup = () => {
    controller.abort();
    ro?.disconnect();
    if (frame) cancelAnimationFrame(frame);
    root.dataset.bestBooted = '0';
  };
}

function initAll(scope = document) {
  scope.querySelectorAll('[data-artink-best]').forEach(bootBest);
}

initAll();
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-artink-best]').forEach((root) => root._artinkBestCleanup?.());
});
